import { Spinner } from '@ohear/ui'
import clsx from 'clsx'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ChatPanel } from '../chat/ChatPanel'
import { LevelMeter } from '../components/LevelMeter'
import { RoleBadge } from '../components/RoleBadge'
import { Panel, SlpButton, StatusBar } from '../components/slp'
import {
  type JoinResponse,
  ApiError,
  getJoin,
  measureDownlink,
  measureRtt,
  measureUplinkMbps,
} from '../lib/api'
import { saveJoinHandoff } from '../lib/join-store'
import { navigate } from '../lib/router'
import { useAudioLevel } from '../lib/use-audio-level'
import { useRtConnection } from '../rt/use-rt-connection'
import '../styles/slp.css'

// ---------------------------------------------------------------------------
// v2.9 grade model — four tiers, weakest-link overall, raw values alongside.
// ---------------------------------------------------------------------------

export type Grade = 'PERFECT' | 'GREAT' | 'OK' | 'POOR'

const GRADE_RANK: Record<Grade, number> = { PERFECT: 0, GREAT: 1, OK: 2, POOR: 3 }

type CheckId = 'browser' | 'samplerate' | 'mic' | 'disk' | 'uplink' | 'downlink' | 'rtt'

interface CheckResult {
  state: 'pending' | 'running' | 'done' | 'na'
  grade: Grade | null
  /** raw value shown right of the badge (`RTT 38 ms` style) */
  detail: string
}

const CHECK_LABELS: Record<CheckId, string> = {
  browser: 'Browser',
  samplerate: 'Sample rate',
  mic: 'Mic signal',
  disk: 'Disk space',
  uplink: 'Uplink bandwidth',
  downlink: 'Downlink bandwidth',
  rtt: 'Network latency',
}

const CHECK_ORDER: CheckId[] = ['browser', 'samplerate', 'mic', 'disk', 'uplink', 'downlink', 'rtt']

/** POOR on these items blocks the Join button (v2.9 table, 阻断 column). */
const BLOCKING: ReadonlySet<CheckId> = new Set(['browser', 'samplerate', 'mic', 'disk'])

/** 2h session × 8.64 MB/min (48k/24-bit mono BWF) × 1.5 safety factor. */
const DISK_NEED_MB = Math.round(2 * 60 * 8.64 * 1.5) // 1555 MB

function isChromium(): boolean {
  const brands = navigator.userAgentData?.brands
  if (brands?.some((b) => /Chromium|Google Chrome|Microsoft Edge/i.test(b.brand))) return true
  return /Chrome\/\d+/.test(navigator.userAgent)
}

function chromiumMajor(): number | null {
  const m = navigator.userAgent.match(/Chrom(?:e|ium)\/(\d+)/)
  return m?.[1] ? Number(m[1]) : null
}

/** Latest-two-majors heuristic for the PERFECT tier (no runtime registry). */
const CHROMIUM_FRESH_MAJOR = 130

type NetColor = 'green' | 'yellow' | 'red'

/** v2.7 #5 — SLP-style guest network verdict. Advisory only. */
function netVerdict(rttMs: number | null, upMbps: number | null): NetColor {
  if (rttMs != null && rttMs <= 150 && upMbps != null && upMbps >= 1) return 'green'
  if ((rttMs != null && rttMs <= 300) || (upMbps != null && upMbps >= 0.5)) return 'yellow'
  return 'red'
}

const GRADE_BADGE: Record<Grade, string> = {
  PERFECT: 'bg-[var(--slp-lamp-green)] text-white shadow-[0_0_8px_rgba(46,204,64,0.6)]',
  GREAT: 'bg-[var(--slp-lamp-green)] text-white',
  OK: 'bg-[var(--slp-lamp-yellow)] text-[#5d4a00]',
  POOR: 'bg-[var(--slp-lamp-red)] text-white',
}

/**
 * Reject after `ms` so a hung browser/API call can never freeze pre-flight on
 * "Checking…" forever — seen in the wild when a DAW holds the audio device
 * exclusively (getUserMedia never settles) or a probe request stalls. A timed
 * out check grades POOR/advisory with a fix hint instead of hanging the page.
 */
function withTimeout<T>(p: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise<T>((res, rej) => {
    const t = window.setTimeout(() => rej(new Error(`${label} timed out after ${ms / 1000} s`)), ms)
    p.then(
      (v) => {
        window.clearTimeout(t)
        res(v)
      },
      (e) => {
        window.clearTimeout(t)
        rej(e)
      },
    )
  })
}

function GradeBadge({
  grade,
  id,
  big,
}: {
  grade: Grade | null
  id?: CheckId
  big?: boolean
}) {
  if (!grade) {
    return (
      <span
        {...(id ? { 'data-testid': `rt-preflight-grade-${id}`, 'data-grade': '' } : {})}
        className={clsx(
          'inline-flex shrink-0 items-center justify-center rounded font-bold uppercase tracking-wider',
          big ? 'h-[28px] px-3 text-[14px]' : 'h-[18px] w-[64px] text-[10px]',
          'bg-[var(--slp-panel-2)] text-[var(--slp-text-dim)]',
        )}
      >
        …
      </span>
    )
  }
  return (
    <span
      {...(id ? { 'data-testid': `rt-preflight-grade-${id}`, 'data-grade': grade } : {})}
      className={clsx(
        'inline-flex shrink-0 items-center justify-center rounded font-bold uppercase tracking-wider',
        big ? 'h-[28px] px-3 text-[14px]' : 'h-[18px] w-[64px] text-[10px]',
        GRADE_BADGE[grade],
      )}
    >
      {grade}
    </span>
  )
}

// ---------------------------------------------------------------------------

export function JoinPage({ token }: { token: string }) {
  // chat re-derives per message — the three connection fns are stable
  // useCallbacks, so effects below depend on THEM (never on the whole value).
  const { chat, connectPreflight, disconnectPreflight, setPreflightGrades } = useRtConnection()

  const [join, setJoin] = useState<JoinResponse | null>(null)
  const [joinError, setJoinError] = useState<string | null>(null)
  const [checks, setChecks] = useState<Record<CheckId, CheckResult>>(() => {
    const init = {} as Record<CheckId, CheckResult>
    for (const id of CHECK_ORDER) init[id] = { state: 'pending', grade: null, detail: '' }
    return init
  })
  const [micStream, setMicStream] = useState<MediaStream | null>(null)
  const [netColor, setNetColor] = useState<NetColor | null>(null)
  const netColorRef = useRef<NetColor | null>(null)
  const netRawRef = useRef<{ rttMs: number | null; upMbps: number | null }>({
    rttMs: null,
    upMbps: null,
  })
  const micStreamRef = useRef<MediaStream | null>(null)
  const micPeakRef = useRef(-100)
  const micDb = useAudioLevel(micStream)
  const runSeq = useRef(0)

  const setCheck = useCallback((id: CheckId, result: CheckResult) => {
    setChecks((prev) => ({ ...prev, [id]: result }))
  }, [])

  // Fetch join info
  useEffect(() => {
    let cancelled = false
    getJoin(token)
      .then((res) => {
        if (!cancelled) setJoin(res)
      })
      .catch((err: unknown) => {
        if (cancelled) return
        // Never leak a raw technical string (e.g. "Request failed (401)") to a
        // voice actor (W1 S2/X1). A bad/expired link surfaces as 401/403/404/410;
        // anything else is a transient can't-reach-the-service problem.
        const status = err instanceof ApiError ? err.status : 0
        setJoinError(
          status === 401 || status === 403 || status === 404 || status === 410
            ? 'This link is invalid or has expired.'
            : "We couldn't open this session right now. Please try again in a moment.",
        )
      })
    return () => {
      cancelled = true
    }
  }, [token])

  const displayName = join?.display_name ?? join?.role ?? 'guest'

  // ---------------------------------------------------------- v2.11: join 页即连房
  // phase='preflight' — chat + grade heartbeat only; nothing published or
  // subscribed. Mock links stay offline (chat works local-only).
  useEffect(() => {
    if (!join || join.mock) return
    connectPreflight({
      url: join.livekit.url,
      token: join.livekit.token,
      role: join.role,
      name: displayName,
    })
  }, [connectPreflight, displayName, join])

  // Resident chat column (v2.11 #1) — expanded by default on this page.
  const openedOnceRef = useRef(false)
  useEffect(() => {
    if (!openedOnceRef.current && join) {
      openedOnceRef.current = true
      chat.setOpen(true)
    }
  }, [chat, join])

  // Leaving /join entirely (NOT the Join click — that disconnects explicitly
  // before navigating, and /room then owns its own connection).
  useEffect(
    () => () => {
      disconnectPreflight()
    },
    [disconnectPreflight],
  )

  // ---------------------------------------------------------------- checks
  const runChecks = useCallback(
    async (info: JoinResponse) => {
      const seq = ++runSeq.current
      const live = () => runSeq.current === seq
      const isActor = info.role === 'actor'

      // 1) Browser — Chromium fresh = PERFECT, older Chromium = GREAT,
      //    non-Chromium = POOR (blocking).
      setCheck('browser', { state: 'running', grade: null, detail: '' })
      if (isChromium()) {
        const major = chromiumMajor()
        const fresh = major != null && major >= CHROMIUM_FRESH_MAJOR
        setCheck('browser', {
          state: 'done',
          grade: fresh ? 'PERFECT' : 'GREAT',
          detail: major != null ? `Chromium ${major}` : 'Chromium',
        })
      } else {
        setCheck('browser', {
          state: 'done',
          grade: 'POOR',
          detail: 'Not Chromium — please use Google Chrome or Microsoft Edge',
        })
      }

      // 2) AudioContext actual sample rate — 48 kHz or blocked (v2.9: =48000
      //    PERFECT, anything else POOR).
      setCheck('samplerate', { state: 'running', grade: null, detail: '' })
      try {
        const ctx = new AudioContext()
        const sr = ctx.sampleRate
        await ctx.close()
        if (!live()) return
        if (sr === 48000) setCheck('samplerate', { state: 'done', grade: 'PERFECT', detail: '48000 Hz' })
        else
          setCheck('samplerate', {
            state: 'done',
            grade: 'POOR',
            detail: `${sr} Hz — set your audio device to 48 kHz`,
          })
      } catch {
        if (!live()) return
        setCheck('samplerate', { state: 'done', grade: 'POOR', detail: 'AudioContext unavailable' })
      }

      // 3) Microphone (actor only) — denied = POOR (blocking). With a stream
      //    the grade tracks the live peak (see the micDb effect below).
      if (isActor) {
        setCheck('mic', { state: 'running', grade: null, detail: '' })
        try {
          const stream = await withTimeout(
            navigator.mediaDevices.getUserMedia({
              audio: {
                echoCancellation: false,
                noiseSuppression: false,
                autoGainControl: false,
                channelCount: 1,
              },
            }),
            20_000,
            'Microphone',
          )
          if (!live()) {
            stream.getTracks().forEach((t) => t.stop())
            return
          }
          micStreamRef.current?.getTracks().forEach((t) => t.stop())
          micStreamRef.current = stream
          micPeakRef.current = -100
          setMicStream(stream)
          setCheck('mic', {
            state: 'done',
            grade: 'OK',
            detail: 'no signal yet — speak into the mic',
          })
        } catch (err) {
          if (!live()) return
          const timedOut = err instanceof Error && /timed out/.test(err.message)
          setCheck('mic', {
            state: 'done',
            grade: 'POOR',
            detail: timedOut
              ? 'Microphone did not respond — another app (e.g. your DAW) may be holding it exclusively. Close it and reload this page.'
              : 'Microphone access was denied',
          })
        }
      } else {
        setCheck('mic', { state: 'na', grade: null, detail: 'Not required for your role' })
      }

      // 4) Disk (actor only) — ratio vs. the estimated need (v2.9 tiers).
      if (isActor) {
        setCheck('disk', { state: 'running', grade: null, detail: '' })
        try {
          const est = await withTimeout(navigator.storage.estimate(), 8_000, 'Storage estimate')
          if (!live()) return
          const availMB = Math.round(((est.quota ?? 0) - (est.usage ?? 0)) / 1_048_576)
          const ratio = availMB / DISK_NEED_MB
          const grade: Grade = ratio >= 3 ? 'PERFECT' : ratio >= 1.5 ? 'GREAT' : ratio >= 1 ? 'OK' : 'POOR'
          const detail =
            grade === 'POOR'
              ? `${availMB.toLocaleString()} MB free — short by ${(DISK_NEED_MB - availMB).toLocaleString()} MB (need ${DISK_NEED_MB.toLocaleString()} MB)`
              : `${availMB.toLocaleString()} MB free / ${DISK_NEED_MB.toLocaleString()} MB needed`
          setCheck('disk', { state: 'done', grade, detail })
        } catch {
          if (!live()) return
          setCheck('disk', { state: 'done', grade: 'POOR', detail: 'Could not read storage estimate' })
        }
      } else {
        setCheck('disk', { state: 'na', grade: null, detail: 'Not required for your role' })
      }

      // 5) Uplink — ≥20 / ≥10 / ≥5 / <5 Mbps (advisory POOR, never blocks).
      setCheck('uplink', { state: 'running', grade: null, detail: '' })
      try {
        const mbps = await withTimeout(measureUplinkMbps(), 15_000, 'Uplink probe')
        if (!live()) return
        netRawRef.current.upMbps = mbps
        const grade: Grade = mbps >= 20 ? 'PERFECT' : mbps >= 10 ? 'GREAT' : mbps >= 5 ? 'OK' : 'POOR'
        setCheck('uplink', { state: 'done', grade, detail: `Upload ~${mbps.toFixed(1)} Mbps` })
      } catch {
        if (!live()) return
        netRawRef.current.upMbps = null
        setCheck('uplink', { state: 'done', grade: 'OK', detail: 'Could not measure (API unreachable)' })
      }

      // 6) Downlink — ≥50 / ≥25 / ≥10 / <10 Mbps. Chrome caps its estimate at
      //    10 Mbps, so a capped reading honestly grades OK ("at least 10").
      setCheck('downlink', { state: 'running', grade: null, detail: '' })
      {
        const dl = measureDownlink()
        if (!live()) return
        if (dl?.capped) {
          // browser-capped reading: we only know "≥10" — honest grade is OK
          setCheck('downlink', {
            state: 'done',
            grade: 'OK',
            detail: 'Download ≥10 Mbps (browser-capped estimate)',
          })
        } else if (dl) {
          const grade: Grade =
            dl.mbps >= 50 ? 'PERFECT' : dl.mbps >= 25 ? 'GREAT' : dl.mbps >= 10 ? 'OK' : 'POOR'
          setCheck('downlink', { state: 'done', grade, detail: `Download ~${dl.mbps} Mbps` })
        } else {
          setCheck('downlink', { state: 'done', grade: 'OK', detail: 'Estimate unavailable' })
        }
      }

      // 7) RTT — median of 5: ≤50 / ≤150 / ≤250 / >250 ms (advisory POOR).
      setCheck('rtt', { state: 'running', grade: null, detail: '' })
      try {
        const rtt = await withTimeout(measureRtt(5), 12_000, 'Latency probe')
        if (!live()) return
        netRawRef.current.rttMs = rtt
        const grade: Grade = rtt <= 50 ? 'PERFECT' : rtt <= 150 ? 'GREAT' : rtt <= 250 ? 'OK' : 'POOR'
        setCheck('rtt', { state: 'done', grade, detail: `RTT ${rtt} ms median` })
      } catch {
        if (!live()) return
        netRawRef.current.rttMs = null
        setCheck('rtt', { state: 'done', grade: 'OK', detail: 'Could not measure (API unreachable)' })
      }

      // combined tri-color verdict → banner + status heartbeat (v2.7 #5 kept)
      const color = netVerdict(netRawRef.current.rttMs, netRawRef.current.upMbps)
      netColorRef.current = color
      setNetColor(color)
    },
    [setCheck],
  )

  useEffect(() => {
    if (join) void runChecks(join)
  }, [join, runChecks])

  // Live mic grading — peak-hold across the whole pre-flight stay.
  useEffect(() => {
    if (!micStream || micDb <= micPeakRef.current) return
    micPeakRef.current = micDb
    const peak = micPeakRef.current
    if (peak <= -60) return // still effectively silent — keep "no signal yet"
    const grade: Grade = peak >= -24 && peak <= -6 ? 'PERFECT' : peak >= -45 ? 'GREAT' : 'OK'
    const hint = peak > -6 ? ' (hot — back off the gain)' : peak < -24 ? ' (low)' : ''
    setCheck('mic', {
      state: 'done',
      grade,
      detail: `peak ${peak.toFixed(1)} dBFS${hint}`,
    })
  }, [micDb, micStream, setCheck])

  // stop preflight mic on unmount
  useEffect(
    () => () => {
      micStreamRef.current?.getTracks().forEach((t) => t.stop())
    },
    [],
  )

  // ------------------------------------------------------------- aggregation
  const applicable = CHECK_ORDER.filter((id) => checks[id].state !== 'na')
  const settled = applicable.every((id) => checks[id].state === 'done')
  const overall: Grade | null = settled
    ? applicable.reduce<Grade>((worst, id) => {
        const g = checks[id].grade ?? 'POOR'
        return GRADE_RANK[g] > GRADE_RANK[worst] ? g : worst
      }, 'PERFECT')
    : null
  const blockers = applicable.filter((id) => BLOCKING.has(id) && checks[id].grade === 'POOR')
  const blocked = blockers.length > 0
  const ready = join !== null && settled && !blocked

  /** grades payload — streamed to the studio + carried into the room.
   *  Memoized on `checks` only so the heartbeat effect fires per CHANGE, not
   *  per render. */
  const grades = useMemo(() => {
    const ids = CHECK_ORDER.filter((id) => checks[id].state !== 'na')
    const done = ids.every((id) => checks[id].state === 'done')
    const g: Record<string, string> = {}
    for (const id of ids) {
      const grade = checks[id].grade
      if (grade) g[id] = grade
    }
    if (done) {
      g.overall = ids.reduce<Grade>((worst, id) => {
        const grade = checks[id].grade ?? 'POOR'
        return GRADE_RANK[grade] > GRADE_RANK[worst] ? grade : worst
      }, 'PERFECT')
    }
    return g
  }, [checks])

  // v2.11 #2 — stream the medical report into the preflight heartbeat.
  useEffect(() => {
    if (Object.keys(grades).length > 0) setPreflightGrades(grades)
  }, [setPreflightGrades, grades])

  // v2.11 #1/#3 — POOR handling: expand chat targeted at the studio; blocking
  // POOR additionally fires the ⚠ system DM so the engineer reaches out first.
  const pooredRef = useRef(false)
  const alertedRef = useRef<Set<CheckId>>(new Set())
  const studioIdentity = chat.recipients.find((r) => r.role === 'studio')?.identity ?? ''
  const anyPoor = applicable.some((id) => checks[id].grade === 'POOR')
  useEffect(() => {
    if (!anyPoor) return
    if (!pooredRef.current) {
      pooredRef.current = true
      chat.setOpen(true)
      if (studioIdentity) chat.setTarget(studioIdentity)
    } else if (studioIdentity && !chat.target) {
      chat.setTarget(studioIdentity)
    }
  }, [anyPoor, chat, studioIdentity])
  useEffect(() => {
    if (!studioIdentity) return
    for (const id of blockers) {
      if (alertedRef.current.has(id)) continue
      alertedRef.current.add(id)
      chat.send(`⚠ ${displayName} pre-flight: ${CHECK_LABELS[id]} POOR — ${checks[id].detail}`, studioIdentity)
    }
  }, [blockers, chat, checks, displayName, studioIdentity])

  const onJoin = async () => {
    if (!join) return
    micStreamRef.current?.getTracks().forEach((t) => t.stop())
    // /room reconnects with the SAME identity — wait for the preflight signal
    // socket to fully close so the duplicate-identity kick can't race it.
    await disconnectPreflight()
    saveJoinHandoff({
      linkToken: token,
      join,
      displayName: join.display_name ?? join.role,
      savedAt: Date.now(),
      ...(netColorRef.current ? { preflight: netColorRef.current } : {}),
      ...(Object.keys(grades).length > 0 ? { grades } : {}),
    })
    navigate('/room')
  }

  // ------------------------------------------------------------------ render
  if (joinError) {
    return (
      <div className="min-h-screen min-w-[1180px] bg-[var(--slp-page)]">
        <div className="mx-auto flex min-h-screen w-[1180px] flex-col justify-center">
          <Panel title="Can't open this session" className="mx-auto w-[472px]">
            <p className="text-[12px] text-[var(--slp-text)]">{joinError}</p>
            <p className="mt-2 text-[12px] text-[var(--slp-text-dim)]">
              Ask your session host for a fresh link.
            </p>
          </Panel>
        </div>
      </div>
    )
  }

  if (!join) {
    return (
      <div className="flex min-h-screen min-w-[1180px] items-center justify-center bg-[var(--slp-page)]">
        <Spinner size="lg" />
      </div>
    )
  }

  const overallText =
    overall === null
      ? 'Running checks…'
      : blocked
        ? `NOT READY — POOR (${blockers.map((id) => CHECK_LABELS[id].toLowerCase()).join(', ')})`
        : overall === 'POOR' || overall === 'OK'
          ? `READY — ${overall} (${applicable
              .filter((id) => checks[id].grade === overall)
              .map((id) => CHECK_LABELS[id].toLowerCase())
              .join(', ')} is the bottleneck)`
          : `READY — ${overall}`

  return (
    <div className="min-h-screen min-w-[1180px] bg-[var(--slp-page)] pb-[36px]">
      <div className="mx-auto w-[1180px] py-4">
        {/* session info bar */}
        <div className="mb-3 flex items-center gap-3">
          <div className="min-w-0 flex-1">
            <p className="text-[11px] uppercase tracking-wide text-[var(--slp-text-dim)]">
              You're invited to
            </p>
            <h1 className="truncate text-[20px] font-semibold tracking-tight text-[var(--slp-text)]">
              {join.session.title}
            </h1>
          </div>
          <RoleBadge role={join.role} />
          {join.session.project_code ? (
            <span className="font-mono text-[12px] text-[var(--slp-text-dim)]">
              {join.session.project_code}
            </span>
          ) : null}
          <span className="text-[12px] text-[var(--slp-text-dim)]">
            {join.session.frame_rate_fps} fps
          </span>
        </div>

        <div className="flex items-start gap-3">
          {/* ── left 2/3: the medical (v2.9) ─────────────────────────────── */}
          <div className="flex w-[778px] flex-col gap-3">
            {blocked ? (
              <div className="rounded-md border-2 border-[var(--slp-lamp-red)] bg-[#fdecec] px-3 py-2 text-[12px] font-semibold text-[#7a1f1f]">
                NOT READY — fix{' '}
                {blockers.map((id) => CHECK_LABELS[id]).join(', ')} below to join. The studio has
                been notified and can help you in the chat.
              </div>
            ) : null}

            <Panel title="Pre-flight Check">
              {/* overall verdict — weakest link wins */}
              <div className="mb-2 flex items-center gap-3 border-b border-[var(--slp-border)] pb-2">
                <span
                  data-testid="rt-preflight-overall"
                  data-grade={overall ?? ''}
                  data-blocked={blocked || undefined}
                  className={clsx(
                    'inline-flex h-[28px] items-center rounded px-3 text-[14px] font-extrabold uppercase tracking-wider',
                    overall === null
                      ? 'bg-[var(--slp-panel-2)] text-[var(--slp-text-dim)]'
                      : blocked
                        ? GRADE_BADGE.POOR
                        : GRADE_BADGE[overall],
                  )}
                >
                  {overallText}
                </span>
              </div>

              <ul className="flex flex-col">
                {CHECK_ORDER.map((id) => {
                  const c = checks[id]
                  const isBlockingPoor = c.grade === 'POOR' && BLOCKING.has(id)
                  return (
                    <li
                      key={id}
                      data-check={id}
                      data-state={c.state}
                      className={clsx(
                        'flex min-h-[28px] flex-col justify-center border-b border-[var(--slp-border)] py-1 last:border-b-0',
                        isBlockingPoor && 'bg-[#fdecec]',
                      )}
                    >
                      <div className="flex items-center gap-2">
                        <span
                          className={clsx(
                            'w-[150px] shrink-0 text-[12px]',
                            c.state === 'na' ? 'text-[var(--slp-text-dim)]' : 'text-[var(--slp-text)]',
                            isBlockingPoor && 'font-bold text-[#7a1f1f]',
                          )}
                        >
                          {CHECK_LABELS[id]}
                        </span>
                        {c.state === 'na' ? (
                          <span className="text-[11px] text-[var(--slp-text-dim)]">{c.detail}</span>
                        ) : (
                          <>
                            <GradeBadge grade={c.state === 'done' ? c.grade : null} id={id} />
                            <span
                              className={clsx(
                                'min-w-0 flex-1 truncate font-mono text-[11px]',
                                c.grade === 'POOR'
                                  ? 'text-[var(--slp-lamp-red)]'
                                  : 'text-[var(--slp-text-dim)]',
                              )}
                              title={c.detail}
                            >
                              {c.state === 'done' ? c.detail : 'Checking…'}
                            </span>
                          </>
                        )}
                        {id === 'mic' && micStream ? (
                          <LevelMeter db={micDb} className="w-[220px] shrink-0" />
                        ) : null}
                      </div>
                      {id === 'samplerate' && c.grade === 'POOR' ? (
                        <div
                          data-testid="rt-preflight-sr"
                          data-state="fail"
                          className="my-1 rounded border border-[var(--slp-lamp-red)] bg-[#fdecec] px-2.5 py-2 text-[11px] text-[#7a1f1f]"
                        >
                          <p className="font-semibold">
                            Recording requires your audio device to run at 48 kHz — joining is
                            blocked until it does.
                          </p>
                          <ul className="mt-1 list-disc pl-4">
                            <li>
                              macOS: open <b>Audio MIDI Setup</b> → select your interface → Format →{' '}
                              <b>48,000 Hz</b>, then reload this page.
                            </li>
                            <li>
                              Windows: Sound settings → device Properties → Advanced →{' '}
                              <b>24 bit, 48000 Hz</b>.
                            </li>
                            <li>
                              If your interface is missing from the list or the rate is locked, a
                              DAW with an exclusive driver (e.g. <b>Pro Tools</b>) may be holding
                              the device — quit the DAW (or disable exclusive mode) and reload.
                            </li>
                          </ul>
                        </div>
                      ) : null}
                    </li>
                  )
                })}
              </ul>

              {netColor ? (
                <div
                  data-testid="rt-preflight-net"
                  data-color={netColor}
                  className={clsx(
                    'mt-3 flex items-start gap-2 rounded border px-2.5 py-1.5 text-[11px]',
                    netColor === 'green' && 'border-[var(--slp-lamp-green)] bg-[#eefaf0] text-[#1d5c2c]',
                    netColor === 'yellow' && 'border-[var(--slp-lamp-yellow)] bg-[#fff8e1] text-[#7a5d00]',
                    netColor === 'red' && 'border-[var(--slp-lamp-red)] bg-[#fdecec] text-[#7a1f1f]',
                  )}
                >
                  <span
                    aria-hidden="true"
                    className={clsx(
                      'mt-0.5 inline-block h-2.5 w-2.5 shrink-0 rounded-full',
                      netColor === 'green'
                        ? 'bg-[var(--slp-lamp-green)]'
                        : netColor === 'yellow'
                          ? 'bg-[var(--slp-lamp-yellow)]'
                          : 'bg-[var(--slp-lamp-red)]',
                    )}
                  />
                  <div>
                    <span className="font-semibold">
                      {netColor === 'green'
                        ? 'Network looks good for a recording session.'
                        : netColor === 'yellow'
                          ? 'Network is workable, but not ideal.'
                          : 'This network may not get you through a recording session.'}
                    </span>{' '}
                    {netColor !== 'green' ? (
                      <span>
                        You can still join — for best results use a wired connection, turn off
                        VPNs, or move closer to the router.
                      </span>
                    ) : null}
                  </div>
                </div>
              ) : null}

              {settled ? (
                <div className="mt-3">
                  <SlpButton
                    variant="secondary"
                    size="sm"
                    onClick={() => join && void runChecks(join)}
                  >
                    Run checks again
                  </SlpButton>
                </div>
              ) : null}
            </Panel>

            {ready ? (
              <div
                data-testid="rt-preflight-ready"
                className="flex items-center justify-between gap-3"
              >
                <p className="text-[12px] text-[var(--slp-text)]">
                  {overall === 'PERFECT' || overall === 'GREAT'
                    ? 'Checks passed. You are good to go.'
                    : 'Checks done — you can join (see the notes above).'}
                </p>
                <SlpButton onClick={onJoin} data-testid="rt-join-button">
                  Join session
                </SlpButton>
              </div>
            ) : (
              <p className="text-[12px] text-[var(--slp-text-dim)]">
                {blocked
                  ? 'The join button unlocks once the blocking items above are fixed.'
                  : 'The join button appears once all checks finish.'}
              </p>
            )}
          </div>

          {/* ── right 1/3: resident studio chat (v2.11) ─────────────────── */}
          <ChatPanel chat={chat} variant="inline" className="h-[560px] w-[390px]" />
        </div>
      </div>
      <StatusBar
        event={
          ready
            ? `Pre-flight ${overall ?? ''} — ready to join`
            : blocked
              ? `Pre-flight blocked: ${blockers.map((id) => CHECK_LABELS[id]).join(', ')}`
              : 'Running pre-flight checks'
        }
        role={join.role}
        className="fixed inset-x-0 bottom-0 z-30"
      />
    </div>
  )
}
